import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import InlineStatusSelect from './InlineStatusSelect';
import ORModal from './ORModal';
import './ReceptionVip.css';
import { PlanificationCell, useDossierPlanning } from './shared/DossierPlanification';
import { libelleStatutDepuis } from '../utils/statutAtelier';
import FactureDetailModal from './shared/FactureDetailModal';

const STATUTS = [
  { valeur: 'recu',             label: 'Reçu',                    couleur: '#3b82f6' },
  { valeur: 'en_diagnostic',    label: 'En diagnostic',          couleur: '#f59e0b' },
  { valeur: 'pieces_commande',  label: 'Pièces en commande',     couleur: '#a855f7' },
  { valeur: 'attente_garantie', label: 'Attente retour garantie', couleur: '#06b6d4' },
  { valeur: 'attente_accord',   label: 'Attente accord client',  couleur: '#ec4899' },
  { valeur: 'pret',             label: 'Prêt',                   couleur: '#10b981' },
  { valeur: 'livre',            label: 'Livré',                  couleur: '#6b7280' },
];

function getStatut(valeur) {
  return STATUTS.find(s => s.valeur === valeur) || { valeur, label: valeur, couleur: '#94a3b8' };
}

function StatutBadge({ valeur }) {
  const s = getStatut(valeur);
  return (
    <span className="statut-badge" style={{ background: s.couleur }}>
      {s.label}
    </span>
  );
}

/* ─── Modal photos lightbox ──────────────────────── */
function Lightbox({ src, onClose }) {
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  return (
    <div className="rv-lightbox-overlay" onClick={onClose}>
      <img src={`data:image/jpeg;base64,${src}`} alt="Photo agrandie" />
    </div>
  );
}

/* ─── Modal détail dossier ──────────────────────── */
function DossierModal({ dossier, onClose, onSaved, onDeleted, isSuperAdmin }) {
  const [photosData, setPhotosData] = useState(null);
  const [photosLoading, setPhotosLoading] = useState(true);
  const [lightboxSrc, setLightboxSrc] = useState(null);

  const [editClientUsername, setEditClientUsername] = useState(dossier.client_username || '');
  const [editStatut, setEditStatut] = useState(dossier.statut || 'recu');
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [saveError, setSaveError] = useState('');

  // Charger les photos séparément
  useEffect(() => {
    const load = async () => {
      try {
        const resp = await api.get(`/reception-vip/${dossier.id}/photos`);
        setPhotosData(resp.data);
      } catch {
        setPhotosData(null);
      } finally {
        setPhotosLoading(false);
      }
    };
    load();
  }, [dossier.id]);

  const handleDelete = async () => {
    if (!window.confirm(`Supprimer définitivement le dossier #${dossier.id} (${dossier.client_username}) ?`)) return;
    setDeleting(true);
    try {
      await api.delete(`/reception-vip/${dossier.id}`);
      onDeleted(dossier.id);
    } catch (err) {
      setSaveError(err.response?.data?.error || 'Erreur lors de la suppression');
    } finally {
      setDeleting(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveError('');
    try {
      await api.put(`/reception-vip/${dossier.id}`, {
        client_username: editClientUsername.trim(),
        statut: editStatut,
      });
      onSaved({ ...dossier, client_username: editClientUsername.trim(), statut: editStatut });
    } catch (err) {
      setSaveError(err.response?.data?.error || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const dateDepot = dossier.created_at
    ? new Date(dossier.created_at).toLocaleString('fr-FR')
    : '—';

  return (
    <>
      <div className="modal-overlay">
        <div className="modal-content" onClick={e => e.stopPropagation()}>
          {/* Header */}
          <div className="modal-header">
            <h2>
              🏁 Dossier #{dossier.id}{dossier.plaque ? ` — ${dossier.plaque}` : ''}
            </h2>
            <button className="modal-close" onClick={onClose} aria-label="Fermer">✕</button>
          </div>

          {/* Body */}
          <div className="modal-body">
            {/* Infos */}
            <div className="rv-info-grid">
              <div className="rv-info-item">
                <label>Client</label>
                <input
                  type="text"
                  value={editClientUsername}
                  onChange={e => setEditClientUsername(e.target.value)}
                  className="rv-inline-input"
                  placeholder="Nom du client"
                />
              </div>
              <div className="rv-info-item">
                <label>Plaque</label>
                <span>
                  {dossier.plaque
                    ? <span className="plaque-badge">{dossier.plaque}</span>
                    : '—'
                  }
                </span>
              </div>
              <div className="rv-info-item">
                <label>Statut actuel</label>
                <span><StatutBadge valeur={dossier.statut} /></span>
              </div>
              <div className="rv-info-item">
                <label>Date dépôt</label>
                <span>{dateDepot}</span>
              </div>
              {dossier.concession_nom && (
                <div className="rv-info-item">
                  <label>Concession</label>
                  <span>{dossier.concession_nom}</span>
                </div>
              )}
            </div>

            {/* Description */}
            {dossier.description && (
              <div className="rv-description-box">{dossier.description}</div>
            )}

            {/* Carte Google Maps */}
            {dossier.latitude && dossier.longitude && (
              <iframe
                className="rv-map-iframe"
                src={`https://maps.google.com/maps?q=${dossier.latitude},${dossier.longitude}&t=k&z=18&output=embed`}
                allowFullScreen
                loading="lazy"
                title="Géolocalisation"
              />
            )}

            {/* Photos */}
            {photosLoading ? (
              <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>Chargement des photos…</p>
            ) : photosData && (photosData.photo_plaque || (photosData.photos_vehicule && photosData.photos_vehicule.length > 0)) ? (
              <div className="rv-photos-section">
                <h4>Photos</h4>
                <div className="rv-photos-grid">
                  {photosData.photo_plaque && (
                    <div
                      className="rv-photo-thumb"
                      title="Photo plaque"
                      onClick={() => setLightboxSrc(photosData.photo_plaque)}
                    >
                      <img src={`data:image/jpeg;base64,${photosData.photo_plaque}`} alt="Plaque" />
                    </div>
                  )}
                  {photosData.photos_vehicule && photosData.photos_vehicule.map((p, i) => (
                    <div
                      key={i}
                      className="rv-photo-thumb"
                      title={`Photo véhicule ${i + 1}`}
                      onClick={() => setLightboxSrc(p)}
                    >
                      <img src={`data:image/jpeg;base64,${p}`} alt={`Véhicule ${i + 1}`} />
                    </div>
                  ))}
                </div>
              </div>
            ) : null}

            {/* Formulaire édition */}
            <div className="rv-edit-form">
              <h4>Mettre à jour le dossier</h4>
              <div className="rv-form-row">
                <div className="rv-form-group">
                  <label>Statut</label>
                  <select value={editStatut} onChange={e => setEditStatut(e.target.value)}>
                    {STATUTS.map(s => (
                      <option key={s.valeur} value={s.valeur}>{s.label}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="modal-footer">
            {saveError && <span className="rv-save-error">{saveError}</span>}
            {isSuperAdmin && (
              <button
                className="btn-danger"
                onClick={handleDelete}
                disabled={deleting || saving}
                style={{ marginRight: 'auto' }}
              >
                {deleting ? 'Suppression…' : '🗑 Supprimer'}
              </button>
            )}
            <button className="btn-secondary" onClick={onClose} disabled={saving || deleting}>
              Fermer
            </button>
            <button className="btn-primary" onClick={handleSave} disabled={saving || deleting}>
              {saving ? 'Enregistrement…' : 'Enregistrer'}
            </button>
          </div>
        </div>
      </div>

      {lightboxSrc && <Lightbox src={lightboxSrc} onClose={() => setLightboxSrc(null)} />}
    </>
  );
}

/* ─── Modale création manuelle ──────────────────── */
/* ─── Renseigner le n° d'OR d'un dossier ───────────
   Bouton « Renseigner » de la colonne N° OR (2026-10-26) : choisir parmi les
   OR du véhicule (même plaque, date d'ouverture puis numéro), ou saisir le
   numéro à la main. */
function RenseignerOrModal({ dossier, onClose, onValider }) {
  const [ors, setOrs] = useState(null); // null = chargement
  const [saisie, setSaisie] = useState('');
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState('');

  useEffect(() => {
    api.get(`/reception-vip/${dossier.id}/ors-candidats`)
      .then(r => setOrs(r.data.ors || []))
      .catch(() => setOrs([]));
  }, [dossier.id]);

  const actuel = dossier.num_or || '';
  // numOr === null : retirer le n° du dossier.
  const valider = async (numOr) => {
    const n = numOr === null ? null : String(numOr || '').trim();
    if (n === '' || (n !== null && n === actuel)) return;
    setEnCours(true);
    setErreur('');
    try {
      await onValider(dossier.id, n);
    } catch (err) {
      setErreur(err.response?.data?.error || "Le n° d'OR n'a pas pu être enregistré.");
      setEnCours(false);
    }
  };

  const dateFr = (iso) => (iso ? iso.split('-').reverse().join('/') : 'date inconnue');

  return (
    <div className="modal-overlay">
      <div className="modal-content small" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{actuel ? "Modifier le n° d'OR" : "Renseigner le n° d'OR"}</h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          {actuel && <p className="rv-or-actuel">N° actuel : <strong>{actuel}</strong></p>}
          <p className="rv-or-intro">
            {dossier.plaque ? <>OR trouvés pour <strong>{dossier.plaque}</strong> :</> : 'Ce dossier n’a pas de plaque : saisissez le numéro.'}
          </p>
          {dossier.plaque && (ors === null ? (
            <p className="rv-or-vide">Recherche des OR…</p>
          ) : ors.length === 0 ? (
            <p className="rv-or-vide">Aucun OR trouvé pour ce véhicule.</p>
          ) : (
            <ul className="rv-or-liste">
              {ors.map(o => (
                <li key={o.num_or}>
                  <button type="button" className={`rv-or-choix${o.num_or === actuel ? ' rv-or-choix--actuel' : ''}`}
                    disabled={enCours || o.num_or === actuel} onClick={() => valider(o.num_or)}>
                    <span className="rv-or-date">{dateFr(o.date_ouverture)}</span>
                    <span className="rv-or-num">{o.num_or}</span>
                    <span className="rv-or-etat">{o.num_or === actuel ? 'actuel' : o.actif ? 'en cours' : 'clos'}</span>
                  </button>
                </li>
              ))}
            </ul>
          ))}
          <div className="rv-form-group">
            <label htmlFor="rv-or-saisie">Ou saisir le numéro</label>
            <input id="rv-or-saisie" className="rv-or-saisie" value={saisie} autoFocus={!dossier.plaque}
              onChange={e => setSaisie(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') valider(saisie); }}
              placeholder="ex. A60037348" />
          </div>
          {erreur && <p className="rv-save-error">{erreur}</p>}
        </div>
        <div className="modal-footer">
          {actuel && (
            <button className="btn-secondary rv-or-retirer" disabled={enCours} onClick={() => valider(null)}>Retirer le n°</button>
          )}
          <button className="btn-secondary" onClick={onClose}>Annuler</button>
          <button className="btn-primary" disabled={enCours || !saisie.trim() || saisie.trim() === actuel} onClick={() => valider(saisie)}>Enregistrer</button>
        </div>
      </div>
    </div>
  );
}

function CreateModal({ onClose, onCreated }) {
  const EMPTY = { client_username: '', plaque: '', num_or: '', description: '', statut: 'recu' };
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.client_username.trim()) { setError('Le nom du client est requis'); return; }
    setSaving(true);
    setError('');
    try {
      const payload = { ...form };
      const r = await api.post('/reception-vip/manual', payload);
      onCreated(r.data.dossier);
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de la création');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content small" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>➕ Nouveau dossier VIP</h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <form onSubmit={handleSubmit} className="modal-body">
          {error && <div style={{ background: 'var(--color-error-light)', color: 'var(--color-error-dark)', borderRadius: 6, padding: '8px 12px', marginBottom: 12, fontSize: 13 }}>{error}</div>}

          <div className="rv-field-row">
            <label>Client <span style={{ color: 'light-dark(var(--color-error), var(--color-error-dark))' }}>*</span></label>
            <input autoFocus value={form.client_username} onChange={e => set('client_username', e.target.value)} placeholder="Nom du client" />
          </div>
          <div className="rv-field-row">
            <label>Plaque</label>
            <input value={form.plaque} onChange={e => set('plaque', e.target.value)} placeholder="AB123CD" style={{ textTransform: 'uppercase' }} />
          </div>
          <div className="rv-field-row">
            <label>N° OR</label>
            <input value={form.num_or} onChange={e => set('num_or', e.target.value)} placeholder="OR-XXXXX" />
          </div>
          <div className="rv-field-row">
            <label>Statut</label>
            <select value={form.statut} onChange={e => set('statut', e.target.value)}>
              {STATUTS.map(s => <option key={s.valeur} value={s.valeur}>{s.label}</option>)}
            </select>
          </div>
          <div className="rv-field-row">
            <label>Description</label>
            <textarea value={form.description} onChange={e => set('description', e.target.value)} rows={2} placeholder="Motif, remarques…" />
          </div>

          <div className="modal-footer">
            <button type="button" className="btn-secondary" onClick={onClose} disabled={saving}>Annuler</button>
            <button type="submit" className="btn-primary" disabled={saving}>{saving ? 'Création…' : 'Créer le dossier'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ─── Modal création/édition client VIP ──────────── */
function ClientModal({ client, concessions, onClose, onSaved }) {
  const isEdit = !!client;
  const [form, setForm] = useState({
    username: client?.username || '',
    password: '',
    nom: client?.nom || '',
    prenom: client?.prenom || '',
    societe: client?.societe || '',
    email: client?.email || '',
    telephone: client?.telephone || '',
    concession_id: client?.concession_id || '',
    actif: client?.actif !== undefined ? client.actif : true,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.username.trim()) { setError("L'identifiant est requis"); return; }
    if (!isEdit && (!form.password || form.password.length < 6)) { setError('Le mot de passe doit contenir au moins 6 caractères'); return; }
    if (!form.nom.trim()) { setError('Le nom est requis'); return; }
    setSaving(true);
    setError('');
    try {
      const payload = { ...form };
      payload.concession_id = payload.concession_id ? parseInt(payload.concession_id) : null;
      if (isEdit && !payload.password) delete payload.password;

      let res;
      if (isEdit) {
        res = await api.put(`/reception-vip/clients/${client.id}`, payload);
      } else {
        res = await api.post('/reception-vip/clients', payload);
      }
      onSaved(res.data.client, isEdit);
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content small" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{isEdit ? '✏️ Modifier le client' : '➕ Nouveau client VIP'}</h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <form onSubmit={handleSubmit} className="modal-body">
          {error && <div style={{ background: 'var(--color-error-light)', color: 'var(--color-error-dark)', borderRadius: 6, padding: '8px 12px', marginBottom: 12, fontSize: 13 }}>{error}</div>}

          <div className="rv-field-row">
            <label>Identifiant <span style={{ color: 'light-dark(var(--color-error), var(--color-error-dark))' }}>*</span></label>
            <input
              autoFocus={!isEdit}
              value={form.username}
              onChange={e => set('username', e.target.value)}
              placeholder="jean.dupont"
              disabled={isEdit}
              style={isEdit ? { background: 'var(--bg-muted)', color: 'var(--text-secondary)' } : {}}
            />
          </div>
          <div className="rv-field-row">
            <label>
              Mot de passe {!isEdit && <span style={{ color: 'light-dark(var(--color-error), var(--color-error-dark))' }}>*</span>}
              {isEdit && <span style={{ fontSize: 11, color: 'var(--text-muted)', marginLeft: 6 }}>(laisser vide pour ne pas changer)</span>}
            </label>
            <input
              type="password"
              value={form.password}
              onChange={e => set('password', e.target.value)}
              placeholder={isEdit ? '••••••' : 'Min. 6 caractères'}
            />
          </div>
          <div className="rv-field-row">
            <label>Nom <span style={{ color: 'light-dark(var(--color-error), var(--color-error-dark))' }}>*</span></label>
            <input value={form.nom} onChange={e => set('nom', e.target.value)} placeholder="Dupont" />
          </div>
          <div className="rv-field-row">
            <label>Prénom</label>
            <input value={form.prenom} onChange={e => set('prenom', e.target.value)} placeholder="Jean" />
          </div>
          <div className="rv-field-row">
            <label>Email</label>
            <input type="email" value={form.email} onChange={e => set('email', e.target.value)} placeholder="jean.dupont@email.com" />
          </div>
          <div className="rv-field-row">
            <label>Téléphone</label>
            <input value={form.telephone} onChange={e => set('telephone', e.target.value)} placeholder="06 12 34 56 78" />
          </div>
          <div className="rv-field-row">
            <label>Société</label>
            <input value={form.societe} onChange={e => set('societe', e.target.value)} placeholder="Nom de la société" />
          </div>
          <div className="rv-field-row">
            <label>Concession</label>
            <select value={form.concession_id} onChange={e => set('concession_id', e.target.value)}>
              <option value="">— Aucune —</option>
              {concessions.map(c => (
                <option key={c.id} value={c.id}>{c.nom}</option>
              ))}
            </select>
          </div>
          {isEdit && (
            <div className="rv-field-row" style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
              <input
                type="checkbox"
                id="client-actif"
                checked={form.actif}
                onChange={e => set('actif', e.target.checked)}
                style={{ width: 'auto' }}
              />
              <label htmlFor="client-actif" style={{ marginBottom: 0, cursor: 'pointer' }}>Compte actif</label>
            </div>
          )}

          <div className="modal-footer">
            <button type="button" className="btn-secondary" onClick={onClose} disabled={saving}>Annuler</button>
            <button type="submit" className="btn-primary" disabled={saving}>
              {saving ? 'Enregistrement…' : (isEdit ? 'Enregistrer' : 'Créer le client')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ─── Composant principal ──────────────────────── */
function ReceptionVip({ user, onOpenVehicule = () => {}, editMode = false, setHeaderAction }) {
  const [view, setView] = useState('aujourd_hui');
  const [dossiers, setDossiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [selectedDossier, setSelectedDossier] = useState(null);
  const [filters, setFilters] = useState({ client: '', statut: '', concession_id: '' });

  const [lastRefresh, setLastRefresh] = useState(null);
  const [concessions, setConcessions] = useState([]);

  // Clients VIP state
  const [clients, setClients] = useState([]);
  const [clientsLoading, setClientsLoading] = useState(false);
  const [showClientModal, setShowClientModal] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [clientSearch, setClientSearch] = useState('');
  const [selectedClientIds, setSelectedClientIds] = useState([]);

  const isAdmin = ['super-admin', 'admin'].includes(user.role);
  const isSuperAdmin = user.role === 'super-admin';

  // Colonne Planification : le dossier du Planning Atelier s'ouvre ici meme,
  // et sa modale porte un bouton pour rejoindre la grille si besoin.
  const { ouvrir: ouvrirDossier, modale: modaleDossier } = useDossierPlanning({
    user, editMode, onEnregistre: () => loadDossiers(),
  });

  // Charger la liste des concessions (pour admin/super-admin)
  useEffect(() => {
    if (!isAdmin) return;
    const load = async () => {
      try {
        const resp = await api.get('/concessions');
        setConcessions(resp.data.concessions || resp.data || []);
      } catch { /* ignore */ }
    };
    load();
  }, [isAdmin]);

  // Charger les clients VIP
  const fetchClients = useCallback(async () => {
    if (!isAdmin) return;
    setClientsLoading(true);
    try {
      const params = {};
      if (clientSearch) params.search = clientSearch;
      const resp = await api.get('/reception-vip/clients', { params });
      setClients(resp.data.clients || []);
    } catch (err) {
      console.error('[ReceptionVip] Erreur chargement clients:', err);
    } finally {
      setClientsLoading(false);
    }
  }, [isAdmin, clientSearch]);

  useEffect(() => {
    if (view === 'clients') fetchClients();
  }, [view, fetchClients]);

  const fetchDossiers = useCallback(async (silent = false) => {
    if (view === 'clients') return;
    if (!silent) setLoading(true);
    setLoadError('');
    try {
      const params = {};
      if (filters.client) params.client = filters.client;
      // Concession du menu (user = utilisateur effectif)
      if (user?.concession_id) params.concession_id = user.concession_id;
      if (view === 'archives') {
        params.archives = 'true';
      } else if (filters.statut) {
        params.statut = filters.statut;
      }
      const resp = await api.get('/reception-vip', { params });
      setDossiers(resp.data.dossiers || []);
      setLastRefresh(new Date());
    } catch (err) {
      console.error('[ReceptionVip] Erreur chargement:', err);
      if (!silent) setLoadError(err.response?.data?.error || err.message || 'Erreur de chargement');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [filters, view, user?.concession_id]);

  const loadDossiers = useCallback(() => fetchDossiers(false), [fetchDossiers]);

  // Chargement initial + rechargement sur changement de vue/filtre
  useEffect(() => {
    if (view !== 'clients') fetchDossiers(false);
  }, [fetchDossiers, view]);

  // Polling silencieux toutes les 30 secondes
  useEffect(() => {
    if (view === 'clients') return;
    const interval = setInterval(() => fetchDossiers(true), 30000);
    return () => clearInterval(interval);
  }, [fetchDossiers, view]);

  // Colonne « Prochaine action » supprimée le 2026-10-26 (champ et donnée) :
  // « À traiter » ne masque plus de dossiers planifiés plus tard.
  const displayedDossiers = dossiers;
  const countAujourdhui = dossiers.length;

  const [showCreate, setShowCreate] = useState(false);
  const [mecaPopover, setMecaPopover] = useState(null);
  const [mecaPopoverPos, setMecaPopoverPos] = useState({ top: 0, right: 0 });

  // Boutons d'action dans le header
  useEffect(() => {
    if (!setHeaderAction) return;
    if (view === 'clients') {
      setHeaderAction({
        primary: { label: '+ Nouveau client', onClick: () => { setEditingClient(null); setShowClientModal(true); } },
      });
    } else {
      setHeaderAction({
        primary: { label: '+ Nouveau dossier', onClick: () => setShowCreate(true) },
      });
    }
  }, [setHeaderAction, view]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCreated = (newDossier) => {
    setDossiers(prev => [newDossier, ...prev]);
    if (view === 'archives') setView('actifs');
  };

  const handleSaved = (updated) => {
    if (updated.statut === 'livre' && view !== 'archives') {
      setDossiers(prev => prev.filter(d => d.id !== updated.id));
      setSelectedDossier(null);
    } else {
      setDossiers(prev => prev.map(d => d.id === updated.id ? { ...d, ...updated } : d));
      setSelectedDossier(prev => prev ? { ...prev, ...updated } : null);
    }
  };

  const handleDeleted = (id) => {
    setDossiers(prev => prev.filter(d => d.id !== id));
    setSelectedDossier(null);
  };

  const handleUpdateStatutInline = async (id, newStatut) => {
    try {
      await api.put(`/reception-vip/${id}`, { statut: newStatut });
      handleSaved({ id, statut: newStatut });
    } catch (err) {
      console.error('[reception-vip] statut inline error:', err);
    }
  };

  const [orRenseigner, setOrRenseigner] = useState(null); // dossier dont on renseigne / modifie l'OR
  // OR ouvert dans la modale OR en cours : { id, dossier } (dossier = celui dont on
  // peut modifier le n° depuis la modale ; null quand on vient d'une facture sans dossier)
  const [orModal, setOrModal] = useState(null);
  const [factureDetail, setFactureDetail] = useState(null);
  const [showFactureDetail, setShowFactureDetail] = useState(false);

  const handleRenseignerOr = async (dossierId, numOr) => {
    await api.patch(`/reception-vip/${dossierId}/num-or`, { num_or: numOr });
    setDossiers(prev => prev.map(d => d.id === dossierId ? { ...d, num_or: numOr } : d));
    setOrRenseigner(null);
    fetchDossiers(true); // statut atelier, facture, badge OR suivent le nouveau numéro
  };

  const handleOpenFacture = async (factureId) => {
    try {
      const res = await api.get(`/factures-services/by-id/${factureId}`);
      setFactureDetail(res.data.facture);
      setShowFactureDetail(true);
    } catch (err) {
      console.error('[reception-vip] facture error:', err);
    }
  };

  const handleDeleteRow = async (e, id, label) => {
    e.stopPropagation();
    if (!window.confirm(`Supprimer définitivement le dossier #${id} (${label}) ?`)) return;
    try {
      await api.delete(`/reception-vip/${id}`);
      handleDeleted(id);
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur lors de la suppression');
    }
  };

  const handleView = (v) => {
    setView(v);
    if (v !== 'clients') {
      setFilters({ client: '', statut: '', concession_id: '' });
    }
  };

  // Client VIP handlers
  const handleClientSaved = (client, isEdit) => {
    if (isEdit) {
      setClients(prev => prev.map(c => c.id === client.id ? { ...c, ...client } : c));
    } else {
      setClients(prev => [client, ...prev]);
    }
  };

  const handleDeleteClients = async () => {
    if (selectedClientIds.length === 0) return;
    const count = selectedClientIds.length;
    if (!window.confirm(`Désactiver ${count} client(s) sélectionné(s) ?`)) return;
    try {
      for (const id of selectedClientIds) {
        await api.delete(`/reception-vip/clients/${id}`);
      }
      setClients(prev => prev.map(c =>
        selectedClientIds.includes(c.id) ? { ...c, actif: false } : c
      ));
      setSelectedClientIds([]);
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur lors de la désactivation');
    }
  };

  const toggleClientSelection = (id) => {
    setSelectedClientIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const toggleAllClients = () => {
    if (selectedClientIds.length === filteredClients.length) {
      setSelectedClientIds([]);
    } else {
      setSelectedClientIds(filteredClients.map(c => c.id));
    }
  };

  const filteredClients = clients.filter(c => {
    if (!clientSearch) return true;
    const s = clientSearch.toLowerCase();
    return (c.username || '').toLowerCase().includes(s)
      || (c.nom || '').toLowerCase().includes(s)
      || (c.prenom || '').toLowerCase().includes(s);
  });

  const STATUTS_ACTIFS = STATUTS.filter(s => s.valeur !== 'livre');

  return (
    <div className="page-container">
      {/* Onglets — toujours en premier */}
      <div className="page-tabs">
        <button
          className={view === 'aujourd_hui' ? 'active' : ''}
          onClick={() => handleView('aujourd_hui')}
        >
          📋 À traiter
          <span className="tab-count">{countAujourdhui}</span>
        </button>
        <button
          className={view === 'actifs' ? 'active' : ''}
          onClick={() => handleView('actifs')}
        >
          🚗 Tous les dossiers
          <span className="tab-count">{dossiers.length}</span>
        </button>
        <button
          className={view === 'archives' ? 'active' : ''}
          onClick={() => handleView('archives')}
        >
          🗄️ Archives
        </button>
        {isAdmin && (
          <button
            className={view === 'clients' ? 'active' : ''}
            onClick={() => handleView('clients')}
          >
            👤 Clients VIP
            <span className="tab-count">{clients.length}</span>
          </button>
        )}
      </div>

      {/* ═══════════ VUE CLIENTS VIP ═══════════ */}
      {view === 'clients' ? (
        <div>
          <div className="page-toolbar">
            <input
              type="text"
              className="page-search"
              placeholder="🔍 Rechercher..."
              value={clientSearch}
              onChange={e => setClientSearch(e.target.value)}
            />
            {editMode && isSuperAdmin && selectedClientIds.length > 0 && (
              <button className="btn-danger" onClick={handleDeleteClients} style={{ marginLeft: 'auto' }}>
                🗑 Désactiver la sélection ({selectedClientIds.length})
              </button>
            )}
          </div>

          {clientsLoading ? (
            <div className="rv-loading">Chargement…</div>
          ) : filteredClients.length === 0 ? (
            <div className="rv-empty">
              <div className="rv-empty-icon">👤</div>
              <p>Aucun client VIP enregistré</p>
            </div>
          ) : (
            <div className="table-container">
              <table className="rv-table">
                <thead>
                  <tr>
                    {editMode && isSuperAdmin && (
                      <th style={{ width: 36 }}>
                        <input
                          type="checkbox"
                          checked={selectedClientIds.length === filteredClients.length && filteredClients.length > 0}
                          onChange={toggleAllClients}
                        />
                      </th>
                    )}
                    <th>Identifiant</th>
                    <th>Société</th>
                    <th>Nom</th>
                    <th>Prénom</th>
                    {isAdmin && <th>Concession</th>}
                    <th>Email</th>
                    <th>Téléphone</th>
                    <th>Statut</th>
                    <th>Créé le</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredClients.map(c => (
                    <tr key={c.id}>
                      {editMode && isSuperAdmin && (
                        <td onClick={e => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            checked={selectedClientIds.includes(c.id)}
                            onChange={() => toggleClientSelection(c.id)}
                          />
                        </td>
                      )}
                      <td style={{ fontWeight: 600, fontFamily: 'monospace' }}>{c.username}</td>
                      <td style={{ fontWeight: 600 }}>{c.societe || <span style={{ color: 'var(--text-muted)' }}>—</span>}</td>
                      <td style={{ fontWeight: 600 }}>{c.nom}</td>
                      <td>{c.prenom || '—'}</td>
                      {isAdmin && <td>{c.concession_nom || <span style={{ color: 'var(--text-muted)' }}>—</span>}</td>}
                      <td style={{ color: 'var(--text-secondary)' }}>{c.email || '—'}</td>
                      <td style={{ color: 'var(--text-secondary)' }}>{c.telephone || '—'}</td>
                      <td>
                        <span
                          className="statut-badge"
                          style={{ background: c.actif ? '#10b981' : '#ef4444' }}
                        >
                          {c.actif ? 'Actif' : 'Inactif'}
                        </span>
                      </td>
                      <td style={{ color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>
                        {c.created_at ? new Date(c.created_at).toLocaleDateString('fr-FR') : '—'}
                      </td>
                      <td>
                        <button
                          className="btn-secondary"
                          style={{ padding: '4px 10px', fontSize: 12 }}
                          onClick={() => { setEditingClient(c); setShowClientModal(true); }}
                        >
                          ✏️ Modifier
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {showClientModal && (
            <ClientModal
              client={editingClient}
              concessions={concessions}
              onClose={() => { setShowClientModal(false); setEditingClient(null); }}
              onSaved={handleClientSaved}
            />
          )}
        </div>
      ) : (
        <>
          {/* ═══════════ VUE DOSSIERS ═══════════ */}

          {/* Filtres */}
          <div className="page-toolbar">
            <input
              type="text"
              className="page-search"
              placeholder="🔍 Rechercher..."
              value={filters.client}
              onChange={e => setFilters(f => ({ ...f, client: e.target.value }))}
            />
            {view !== 'archives' && (
              <select
                className={`page-select${filters.statut ? ' active-filter' : ''}`}
                value={filters.statut}
                onChange={e => setFilters(f => ({ ...f, statut: e.target.value }))}
              >
                <option value="">Tous les statuts</option>
                {STATUTS_ACTIFS.map(s => (
                  <option key={s.valeur} value={s.valeur}>{s.label}</option>
                ))}
              </select>
            )}
            {/* Concession : choix du menu (2026-10-26), plus de liste propre à la page. */}
          </div>

          {/* Erreur chargement */}
          {loadError && (
            <div style={{ background: 'var(--color-error-light)', color: 'var(--color-error-dark)', border: '1px solid light-dark(#fca5a5, var(--border-strong))', borderRadius: '8px', padding: '12px 16px', margin: '0 0 12px', fontSize: '13px', fontWeight: 600 }}>
              ❌ Erreur : {loadError}
            </div>
          )}

          {/* Contenu */}
          {loading ? (
            <div className="rv-loading">Chargement…</div>
          ) : displayedDossiers.length === 0 ? (
            <div className="rv-empty">
              <div className="rv-empty-icon">{view === 'archives' ? '📁' : '🏁'}</div>
              <p>
                {view === 'archives' ? 'Aucun véhicule livré en archives'
                  : view === 'aujourd_hui' ? '✅ Aucun dossier à traiter aujourd\'hui'
                  : 'Aucun dossier VIP en cours'}
              </p>
            </div>
          ) : (
            <div className="table-container">
              <table className="rv-table">
                <thead>
                  <tr>
                    <th>Dépôt</th>
                    <th>Véhicule</th>
                    <th>Client</th>
                    <th>N° OR</th>
                    <th title="Véhicule sur le parking (inventaire)" aria-label="Parking" style={{ textAlign: 'center' }}>🅿️</th>
                    <th title="Statut du dossier vu par le client VIP">Statut client</th>
                    <th title="Statut du dossier dans le Planning Atelier (rattaché par le n° d'OR)">Statut atelier</th>
                    <th>Facture</th>
                    {isSuperAdmin && <th>Planification</th>}
                    {isSuperAdmin && <th></th>}
                  </tr>
                </thead>
                <tbody>
                  {displayedDossiers.map(d => {
                    return (
                    <tr key={d.id} onClick={() => setSelectedDossier(d)}>
                      <td style={{ color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>
                        {d.created_at ? new Date(d.created_at).toLocaleDateString('fr-FR') : '—'}
                      </td>
                      <td onClick={e => { e.stopPropagation(); if (d.plaque) onOpenVehicule(d.plaque); }}>
                        {d.plaque
                          ? <div>
                              <span className="plaque-badge plaque-badge-link" title="Voir la fiche véhicule">{d.plaque}</span>
                              {d.marque && <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginTop: '2px', fontWeight: '500' }}>{d.marque}</div>}
                            </div>
                          : <span style={{ color: 'var(--text-muted)' }}>—</span>
                        }
                      </td>
                      <td style={{ fontWeight: 600 }}>
                        {d.client_societe || (d.client_nom ? `${d.client_nom}${d.client_prenom ? ` ${d.client_prenom}` : ''}` : d.client_username)}
                      </td>
                      <td onClick={e => e.stopPropagation()} style={{ whiteSpace: 'nowrap', minWidth: '90px' }}>
                        {/* Le n° d'OR s'ouvre d'un clic (2026-10-26, remplace le badge « OR » et
                            la saisie en ligne) : OR encore en cours ⇒ sa fiche, d'où l'on peut
                            modifier le n° ; OR clos ⇒ directement la fenêtre de modification. */}
                        {d.num_or ? (
                          <button type="button" className="rv-num-or-value"
                            onClick={() => (d.or_en_cours_id ? setOrModal({ id: d.or_en_cours_id, dossier: d }) : setOrRenseigner(d))}
                            title={d.or_en_cours_id ? "Ouvrir l'OR" : "Modifier le n° d'OR"}>
                            {d.num_or}
                          </button>
                        ) : (
                          <button type="button" className="rv-btn-renseigner" onClick={() => setOrRenseigner(d)}>
                            Renseigner
                          </button>
                        )}
                      </td>
                      <td style={{ textAlign: 'center' }} title={d.in_parking ? 'Véhicule sur le parking (inventaire)' : undefined}>
                        {d.in_parking ? '🅿️' : ''}
                      </td>
                      <td onClick={e => e.stopPropagation()}>
                        <InlineStatusSelect
                          value={d.statut}
                          options={STATUTS.map(s => ({ value: s.valeur, label: s.label, color: s.couleur }))}
                          onChange={newStatut => handleUpdateStatutInline(d.id, newStatut)}
                        />
                      </td>
                      {/* Statut atelier : même pastille que Gestion Parking (couleur du
                          statut, « depuis le … »), cliquable pour ouvrir le dossier. */}
                      <td style={{ whiteSpace: 'nowrap', width: '1%', padding: '4px 8px' }}
                        onClick={e => { if (d.statut_atelier_nom && d.planning_intervention_id && isSuperAdmin) e.stopPropagation(); }}>
                        {d.statut_atelier_nom ? (
                          <span className={`parking-statut-atelier${d.planning_intervention_id && isSuperAdmin ? ' parking-statut-atelier--btn' : ''}`}
                            role={d.planning_intervention_id && isSuperAdmin ? 'button' : undefined}
                            tabIndex={d.planning_intervention_id && isSuperAdmin ? 0 : undefined}
                            onClick={d.planning_intervention_id && isSuperAdmin ? () => ouvrirDossier(d) : undefined}
                            onKeyDown={d.planning_intervention_id && isSuperAdmin ? (e => { if (e.key === 'Enter') ouvrirDossier(d); }) : undefined}
                            style={{ background: d.statut_atelier_couleur || 'var(--bg-muted)', color: d.statut_atelier_text_color || 'var(--text-primary)' }}
                            title={d.planning_intervention_id && isSuperAdmin ? 'Ouvrir le dossier du Planning Atelier' : 'Statut du Planning Atelier'}>
                            {d.statut_atelier_nom}
                            {(() => {
                              const l = libelleStatutDepuis(d.statut_atelier_nom, d.statut_atelier_since, d.statut_atelier_since_estime);
                              return l ? <span className="parking-statut-atelier-depuis"> {l}</span> : null;
                            })()}
                          </span>
                        ) : <span style={{ color: 'var(--text-muted)' }}>—</span>}
                      </td>
                      <td onClick={e => e.stopPropagation()} style={{ whiteSpace: 'nowrap' }}>
                        {/* La date de facture ouvre elle-même le détail (2026-10-26, remplace le badge FAC). */}
                        {d.facture_id && d.facture_date ? (
                          <button type="button" className="rv-facture-date" title="Voir la facture"
                            onClick={() => handleOpenFacture(d.facture_id)}>
                            {new Date(d.facture_date).toLocaleDateString('fr-FR')}
                          </button>
                        ) : d.facture_date ? (
                          <span>{new Date(d.facture_date).toLocaleDateString('fr-FR')}</span>
                        ) : <span style={{ color: 'var(--text-muted)' }}>—</span>}
                      </td>
                      {isSuperAdmin && (
                        <td onClick={e => e.stopPropagation()} style={{ whiteSpace: 'nowrap' }}>
                          <PlanificationCell dossier={d} onOuvrir={ouvrirDossier} />
                        </td>
                      )}
                      {isSuperAdmin && (
                        <td onClick={e => e.stopPropagation()} style={{ textAlign: 'center', padding: '0 8px' }}>
                          <button
                            className="btn-icon delete"
                            onClick={e => handleDeleteRow(e, d.id, d.client_username)}
                            title="Supprimer"
                          >🗑️</button>
                        </td>
                      )}
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {showCreate && (
            <CreateModal onClose={() => setShowCreate(false)} onCreated={handleCreated} />
          )}

          {selectedDossier && (
            <DossierModal
              dossier={selectedDossier}
              onClose={() => setSelectedDossier(null)}
              onSaved={handleSaved}
              onDeleted={handleDeleted}
              isSuperAdmin={isSuperAdmin}
            />
          )}

          {orRenseigner && (
            <RenseignerOrModal
              dossier={orRenseigner}
              onClose={() => setOrRenseigner(null)}
              onValider={handleRenseignerOr}
            />
          )}

          {orModal && (
            <ORModal
              orId={orModal.id}
              user={user}
              onClose={() => setOrModal(null)}
              onModifierNumero={orModal.dossier ? () => { const dos = orModal.dossier; setOrModal(null); setOrRenseigner(dos); } : undefined}
            />
          )}

          {showFactureDetail && factureDetail && (
            <FactureDetailModal facture={factureDetail} onClose={() => setShowFactureDetail(false)} onOuvrirOr={(() => {
                const dos = dossiers.find(x => x.num_or && x.num_or === factureDetail.num_or && x.or_en_cours_id);
                return dos ? () => { setShowFactureDetail(false); setOrModal({ id: dos.or_en_cours_id, dossier: dos }); } : null;
              })()} />
          )}
        </>
      )}
      {modaleDossier}
    </div>
  );
}

export default ReceptionVip;
